import {Button, Icon, icon, Modal, ModalContent, ModalFooter, ModalTitle} from '@hm/fundamental'
import React from 'react'

export type ConfirmationModalVariant = 'destructive' | 'warning' | 'default'

interface ConfirmationModalButton {
  text: string
  icon: string
  onClick: () => void
}

interface ConfirmationModalProps {
  open: boolean
  title: string
  message: string
  cancelButton: ConfirmationModalButton
  secondaryButton?: ConfirmationModalButton
  confirmButton: ConfirmationModalButton
  variant?: ConfirmationModalVariant
  onClose?: () => void
  'data-test-id'?: string
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  title,
  message,
  cancelButton,
  secondaryButton,
  confirmButton,
  variant = 'default',
  onClose,
  'data-test-id': dataTestId = 'confirmation-modal'
}) => {
  const getConfirmButtonVariant = () => {
    switch (variant) {
      case 'destructive':
        return 'negative'
      case 'warning':
        return 'primary'
      default:
        return 'primary'
    }
  }

  return (
    <Modal open={open} onClose={onClose || cancelButton.onClick} data-test-id={dataTestId}>
      <div className="relative">
        <button
          onClick={onClose || cancelButton.onClick}
          className="absolute top-0 right-0 p-2 hover:opacity-70 transition-opacity"
          aria-label="Close"
          data-test-id={`${dataTestId}-close`}
        >
          <Icon path={icon.mdiClose} size="regular" className="text-foreground" />
        </button>
        <ModalTitle data-test-id={`${dataTestId}-title`}>{title}</ModalTitle>
        <ModalContent>{message}</ModalContent>
        <ModalFooter>
          <Button
            variant="secondary"
            size="regular"
            onClick={cancelButton.onClick}
            data-test-id={`${dataTestId}-cancel`}
          >
            <Icon path={cancelButton.icon} size="regular" />
            {cancelButton.text}
          </Button>
          {secondaryButton && (
            <Button
              variant="tertiary"
              size="regular"
              onClick={secondaryButton.onClick}
              data-test-id={`${dataTestId}-secondary`}
            >
              <Icon path={secondaryButton.icon} size="regular" />
              {secondaryButton.text}
            </Button>
          )}
          <Button
            variant={getConfirmButtonVariant()}
            size="regular"
            onClick={confirmButton.onClick}
            data-test-id={`${dataTestId}-confirm`}
          >
            <Icon path={confirmButton.icon} size="regular" />
            {confirmButton.text}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  )
}
